import { useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { Button } from '../../components/ui';

interface IntelligenceSettings {
  pipelineEnabled: boolean;
  anomalyDetectionEnabled: boolean;
  insightsEnabled: boolean;
  retentionDays: number;
  anomalySensitivity: 'low' | 'medium' | 'high';
  alertEmail: string;
  alertSlack: string;
  llmBudgetLimit: number;
  autoArchiveDays: number;
}

const saving = signal(false);
const saved = signal(false);

const toggleCls = (on: boolean) =>
  `relative w-11 h-6 rounded-full transition-colors cursor-pointer ${on ? 'bg-indigo-600' : 'bg-gray-700'}`;

const dotCls = (on: boolean) =>
  `absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-5' : ''}`;

const inputCls = 'bg-gray-800 border border-gray-700 text-gray-200 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 outline-none w-full';
const selectCls = inputCls;

function SettingsInner() {
  const [settings, setSettings] = useState<IntelligenceSettings>({
    pipelineEnabled: true,
    anomalyDetectionEnabled: true,
    insightsEnabled: true,
    retentionDays: 90,
    anomalySensitivity: 'medium',
    alertEmail: '',
    alertSlack: '',
    llmBudgetLimit: 100,
    autoArchiveDays: 30,
  });

  const update = (patch: Partial<IntelligenceSettings>) => setSettings({ ...settings, ...patch });

  async function handleSave() {
    saving.value = true;
    // Simulate save
    await new Promise((r) => setTimeout(r, 500));
    saving.value = false;
    saved.value = true;
    setTimeout(() => { saved.value = false; }, 2000);
  }

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Configurações</h2>

      <div class="space-y-6 max-w-2xl">
        {/* Pipeline */}
        <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 class="text-sm font-medium text-white mb-4">Pipeline de Dados</h3>

          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm text-gray-200">Pipeline Habilitado</div>
                <div class="text-xs text-gray-500">Processar eventos e gerar agregados</div>
              </div>
              <div class={toggleCls(settings.pipelineEnabled)} onClick={() => update({ pipelineEnabled: !settings.pipelineEnabled })}>
                <div class={dotCls(settings.pipelineEnabled)} />
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm text-gray-200">Detecção de Anomalias</div>
                <div class="text-xs text-gray-500">Detectar comportamentos anômalos automaticamente</div>
              </div>
              <div class={toggleCls(settings.anomalyDetectionEnabled)} onClick={() => update({ anomalyDetectionEnabled: !settings.anomalyDetectionEnabled })}>
                <div class={dotCls(settings.anomalyDetectionEnabled)} />
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm text-gray-200">Geração de Insights</div>
                <div class="text-xs text-gray-500">Gerar insights automaticamente</div>
              </div>
              <div class={toggleCls(settings.insightsEnabled)} onClick={() => update({ insightsEnabled: !settings.insightsEnabled })}>
                <div class={dotCls(settings.insightsEnabled)} />
              </div>
            </div>
          </div>
        </div>

        {/* Data Retention */}
        <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 class="text-sm font-medium text-white mb-4">Retenção de Dados</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-xs text-gray-400 mb-1 block">Retenção (dias)</label>
              <input type="number" class={inputCls} value={settings.retentionDays} onChange={(e) => update({ retentionDays: parseInt((e.target as HTMLInputElement).value) || 90 })} />
            </div>
            <div>
              <label class="text-xs text-gray-400 mb-1 block">Auto-archive (dias)</label>
              <input type="number" class={inputCls} value={settings.autoArchiveDays} onChange={(e) => update({ autoArchiveDays: parseInt((e.target as HTMLInputElement).value) || 30 })} />
            </div>
          </div>
        </div>

        {/* Anomaly Detection */}
        <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 class="text-sm font-medium text-white mb-4">Detecção de Anomalias</h3>
          <div>
            <label class="text-xs text-gray-400 mb-1 block">Sensibilidade</label>
            <select class={selectCls} value={settings.anomalySensitivity} onChange={(e) => update({ anomalySensitivity: (e.target as HTMLSelectElement).value as any })}>
              <option value="low">Baixa — menos alertas</option>
              <option value="medium">Média — balanceado</option>
              <option value="high">Alta — mais alertas</option>
            </select>
          </div>
        </div>

        {/* Alerts */}
        <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 class="text-sm font-medium text-white mb-4">Alertas</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-xs text-gray-400 mb-1 block">Email</label>
              <input type="email" class={inputCls} value={settings.alertEmail} placeholder="admin@example.com" onInput={(e) => update({ alertEmail: (e.target as HTMLInputElement).value })} />
            </div>
            <div>
              <label class="text-xs text-gray-400 mb-1 block">Slack Webhook</label>
              <input type="url" class={inputCls} value={settings.alertSlack} placeholder="https://hooks.slack.com/..." onInput={(e) => update({ alertSlack: (e.target as HTMLInputElement).value })} />
            </div>
          </div>
        </div>

        {/* LLM Budget */}
        <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 class="text-sm font-medium text-white mb-4">Orçamento LLM</h3>
          <div>
            <label class="text-xs text-gray-400 mb-1 block">Limite mensal (USD)</label>
            <input type="number" class={inputCls} value={settings.llmBudgetLimit} onChange={(e) => update({ llmBudgetLimit: parseFloat((e.target as HTMLInputElement).value) || 100 })} />
          </div>
        </div>

        {/* Save */}
        <div class="flex items-center gap-3">
          <Button variant="ghost"
            class="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving.value}
          >
            {saving.value ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
 {saved.value && <span class="text-sm text-green-400">Salvo!</span>}
        </div>
      </div>
    </div>
  );
}

export function SettingsPanel() {
  return <SettingsInner />;
}
